// src/pages/services/VLSIServices.jsx - Premium VLSI Design Services
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Cpu, Layers, ArrowRight, ArrowLeft, Factory, Zap, Activity, Settings } from 'lucide-react';
import RelatedServices from '../../components/RelatedServices';


export default function VLSIServices() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const designServices = [
    { 
      category: 'Frontend Design', 
      items: ['RTL Coding (SystemVerilog/VHDL)', 'Logic Synthesis & Optimization', 'Static Timing Analysis', 'Formal Verification', 'Lint & CDC Checks'],
      color: 'from-purple-500 to-purple-600',
      icon: '💻'
    },
    { 
      category: 'Backend Design', 
      items: ['Advanced Floorplanning', 'Placement Optimization', 'Clock Tree Synthesis', 'Routing & Timing Closure', 'Physical Verification (DRC/LVS)'],
      color: 'from-indigo-500 to-indigo-600',
      icon: '🗺️'
    },
    { 
      category: 'Verification', 
      items: ['UVM Testbench Development', 'Coverage-Driven Verification', 'Functional & Formal Verification', 'Regression Testing', 'Performance Validation'],
      color: 'from-blue-500 to-blue-600',
      icon: '✅'
    },
    { 
      category: 'DFT Services', 
      items: ['Scan Insertion & ATPG', 'Built-in Self-Test (BIST)', 'Boundary Scan (JTAG)', 'Fault Simulation', 'Test Coverage Analysis'],
      color: 'from-violet-500 to-violet-600',
      icon: '🔧'
    }
  ];

  const technologyNodes = [
    { node: '180nm', status: 'Production', applications: 'Legacy designs, IoT, Power mgmt' },
    { node: '90nm/65nm', status: 'Mature', applications: 'Consumer electronics, Medical' },
    { node: '45nm/28nm', status: 'Mainstream', applications: 'Mobile, Automotive, Industrial' },
    { node: '22nm/16nm', status: 'Advanced', applications: 'High-performance, Data center' },
    { node: '7nm/5nm', status: 'Cutting-edge', applications: 'AI/ML, Gaming, 5G/6G chips' }
  ];

  const edaTools = [
    { vendor: 'Synopsys', tools: 'Design Compiler, PrimeTime, IC Compiler, VCS', icon: '🔧' },
    { vendor: 'Cadence', tools: 'Genus, Innovus, Spectre, Xcelium, Conformal', icon: '⚙️' },
    { vendor: 'Siemens EDA', tools: 'Questa, Calibre, Tessent, ModelSim', icon: '🛠️' },
    { vendor: 'Xilinx/AMD', tools: 'Vivado, Vitis, Ultrascale Architecture', icon: '🔷' },
    { vendor: 'Intel/Altera', tools: 'Quartus Prime, Max V, Stratix Devices', icon: '🔶' }
  ];

  const projectTypes = [
    { type: 'Communication ICs', examples: ['Ethernet PHY', 'SerDes 100Gbps+', 'Wireless Transceivers'], complexity: 'Very High' },
    { type: 'Memory Controllers', examples: ['DDR4/DDR5 PHY', 'LPDDR5/6', 'Flash Controllers'], complexity: 'Very High' },
    { type: 'Processing Units', examples: ['DSP Cores', 'GPU/NPU', 'Custom Processors', 'RISC-V CPUs'], complexity: 'Very High' },
    { type: 'Power Management', examples: ['PMIC Design', 'Voltage Regulators', 'Battery Management IC'], complexity: 'High' }
  ];

  const stats = [
    { value: '50+', label: 'Tape-outs Completed', icon: '🎯' },
    { value: '100M+', label: 'Gates Designed', icon: '🔌' },
    { value: '15+', label: 'Technology Nodes', icon: '⚡' },
    { value: '99%', label: 'First-Pass Success', icon: '✓' }
  ];

  const capabilities = [
    { title: 'Design Entry', desc: 'SystemVerilog, VHDL, HLS', icon: '💾' },
    { title: 'Simulation', desc: 'Behavioral, RTL, Gate-level', icon: '🔄' },
    { title: 'Synthesis', desc: 'Logic & physical synthesis', icon: '⚙️' },
    { title: 'Signoff', desc: 'DRC, LVS, PV ready', icon: '✓' },
    { title: 'Tape-out', desc: 'GDS delivery & support', icon: '🎬' },
    { title: 'Support', desc: '24/7 technical assistance', icon: '🔧' }
  ];

  const pricingPlans = [
    {
      name: 'RTL Design',
      price: 999999,
      monthlySupport: 9999,
      color: 'purple',
      badge: null,
      features: [
        'SystemVerilog/VHDL Coding',
        'Basic Verification Testbenches',
        'Synthesis & Optimization',
        'Static Timing Analysis',
        'IP Package & Documentation',
        '3 Months Support Included'
      ]
    },
    {
      name: 'Physical Design',
      price: 1999999,
      monthlySupport: 19999,
      color: 'indigo',
      badge: 'MOST POPULAR',
      featured: true,
      features: [
        'Advanced Floorplanning',
        'Place & Route Implementation',
        'Clock Tree Synthesis',
        'Timing & Power Closure',
        'Physical Verification Ready',
        '4 Months Support Included',
        'Multi-core SoC Support'
      ]
    },
    {
      name: 'Complete ASIC',
      price: 4999999,
      monthlySupport: 49999,
      color: 'blue',
      badge: null,
      features: [
        'Full RTL to GDSII Flow',
        'Comprehensive UVM Verification',
        'Advanced Synthesis & STA',
        'Physical Design & Optimization',
        'Power & Thermal Analysis',
        '6 Months Support Included',
        'Tape-out Guidance & Support',
        'Sign-off Ready GDS'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-600 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/services" className="inline-flex items-center gap-2 text-white hover:underline mb-4 sm:mb-6 text-xs sm:text-base font-medium">
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" /> Back to Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 leading-tight">
                VLSI Software & Design Services
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-purple-100 mb-6 sm:mb-8 leading-relaxed">
                Comprehensive VLSI design services from RTL to GDSII. Building India's world-class semiconductor ecosystem with cutting-edge expertise.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                <Link to="/contact" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base shadow-lg">
                  Start VLSI Project
                </Link>
                <Link to="/portfolio" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center text-sm sm:text-base">
                  View VLSI Portfolio
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&h=400&fit=crop" alt="VLSI Design" className="rounded-2xl shadow-2xl w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 sm:py-16 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center bg-white dark:bg-gray-900 p-3 sm:p-6 rounded-xl shadow-lg hover:shadow-xl transition border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-2xl sm:text-4xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform">{stat.icon}</div>
                <div className="text-2xl sm:text-4xl font-black text-purple-600 mb-1 sm:mb-2 group-hover:scale-110 transition-transform">{stat.value}</div>
                <div className="text-xs sm:text-lg font-bold text-gray-900 dark:text-white">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Services */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Comprehensive Design Services
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-2">
              End-to-end VLSI design expertise across all phases
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {designServices.map((category, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">{category.icon}</div>
                <div className={`inline-block px-3 sm:px-4 py-1 sm:py-2 bg-gradient-to-r ${category.color} text-white rounded-lg mb-3 sm:mb-4 font-bold text-xs sm:text-sm`}>
                  {category.category}
                </div>
                <ul className="space-y-1.5 sm:space-y-2">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-400">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Design Capabilities
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-6">
            {capabilities.map((cap, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 p-4 sm:p-6 rounded-xl shadow-lg text-center hover:scale-105 transition-all border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-3xl sm:text-4xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform">{cap.icon}</div>
                <h3 className="text-base sm:text-lg font-bold mb-1 sm:mb-2 text-gray-900 dark:text-white">{cap.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Nodes */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Technology Node Expertise
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 px-2">
              From legacy to cutting-edge process nodes
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6">
            {technologyNodes.map((tech, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition text-center">
                <div className="text-2xl sm:text-3xl font-bold text-purple-600 mb-2 sm:mb-3">{tech.node}</div>
                <div className="inline-block px-2 sm:px-3 py-1 text-white rounded-full text-xs font-semibold mb-2 sm:mb-3 bg-gradient-to-r from-purple-500 to-indigo-500">
                  {tech.status}
                </div>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">{tech.applications}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EDA Tools */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            EDA Tool Expertise
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 font-normal mt-2">Industry-standard tools and platforms</p>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-6">
            {edaTools.map((vendor, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 p-4 sm:p-6 rounded-xl shadow-lg text-center hover:scale-105 transition border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-4xl sm:text-5xl mb-2 sm:mb-3 group-hover:scale-110 transition-transform">{vendor.icon}</div>
                <h3 className="text-sm sm:text-lg font-bold mb-2 sm:mb-3 text-purple-600">{vendor.vendor}</h3>
                <p className="text-xs text-gray-600 dark:text-gray-300 leading-tight">{vendor.tools}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Project Types We Handle
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 px-2">
              Diverse VLSI applications across industries
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
            {projectTypes.map((project, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition border border-gray-200/50 dark:border-gray-700/50">
                <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
                  <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white flex-1">{project.type}</h3>
                  <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${
                    project.complexity === 'Very High' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                    'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300'
                  }`}>
                    {project.complexity}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {project.examples.map((example, idx) => (
                    <div key={idx} className="bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 px-2 sm:px-3 py-1 sm:py-2 rounded text-xs sm:text-sm text-center font-medium">
                      {example}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Future Vision */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-orange-500 via-amber-500 to-green-600 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <Factory className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-6 animate-bounce" />
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
              🇮🇳 Future Vision: Semiconductor Fabrication
            </h2>
            <p className="text-base sm:text-xl max-w-4xl mx-auto mb-6 sm:mb-8 leading-relaxed px-2">
              Strategic roadmap: Building India's advanced semiconductor manufacturing capability. 
              7nm, 5nm process technology ecosystem with global standards.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 hover:shadow-2xl transition-all">
              <h3 className="text-lg sm:text-2xl font-bold mb-3 sm:mb-4">R&D Phase (2030-2032)</h3>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li>✓ Advanced process technology research</li>
                <li>✓ Equipment partnerships & collaborations</li>
                <li>✓ Talent acquisition & training</li>
                <li>✓ Lab infrastructure setup</li>
              </ul>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 hover:shadow-2xl transition-all">
              <h3 className="text-lg sm:text-2xl font-bold mb-3 sm:mb-4">Pilot Fab (2033-2034)</h3>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li>✓ Small-scale production capacity</li>
                <li>✓ Process validation & optimization</li>
                <li>✓ Yield improvement cycles</li>
                <li>✓ Customer sampling & evaluation</li>
              </ul>
            </div>
            
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-white/20 hover:shadow-2xl transition-all">
              <h3 className="text-lg sm:text-2xl font-bold mb-3 sm:mb-4">Commercial Fab (2035+)</h3>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li>✓ High-volume manufacturing</li>
                <li>✓ 7nm/5nm commercial production</li>
                <li>✓ Global customer base</li>
                <li>✓ International export capabilities</li>
              </ul>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white/10 backdrop-blur-sm px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-center border border-white/20">
              <div className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">₹500Cr+</div>
              <div className="text-xs sm:text-sm">R&D Investment</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-center border border-white/20">
              <div className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">5000+</div>
              <div className="text-xs sm:text-sm">High-skill Jobs</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 sm:px-6 py-3 sm:py-4 rounded-lg text-center border border-white/20">
              <div className="text-2xl sm:text-4xl font-bold mb-1 sm:mb-2">ISO Class 1</div>
              <div className="text-xs sm:text-sm">Cleanroom Standards</div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-gray-50 to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-3 sm:mb-4">
              VLSI Service Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2 leading-relaxed">
              Flexible pricing for VLSI projects. From RTL design to complete ASIC tape-out.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12 sm:mb-20">
            {pricingPlans.map((plan, idx) => {
              const colorMap = {
                purple: { bg: 'from-purple-600 to-indigo-600', light: 'from-purple-50 to-indigo-50', hover: 'hover:border-purple-200' },
                indigo: { bg: 'from-indigo-600 to-blue-600', light: 'from-indigo-50 to-blue-50', hover: 'hover:border-indigo-200' },
                blue: { bg: 'from-blue-600 to-cyan-600', light: 'from-blue-50 to-cyan-50', hover: 'hover:border-blue-200' }
              };
              const colors = colorMap[plan.color];

              return (
                <div
                  key={idx}
                  className={`group relative ${plan.featured ? 'md:scale-105' : ''} bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col ${colors.hover} dark:hover:border-gray-600/50 ${plan.featured ? 'border-2 border-indigo-500 dark:border-indigo-600' : ''}`}
                >
                  {plan.featured && (
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-blue-500/10 dark:from-indigo-600/20 dark:to-blue-600/20 rounded-2xl opacity-100 -z-10 blur-xl"></div>
                  )}

                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`bg-gradient-to-r ${colors.bg} text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg`}>
                        {plan.badge} ⭐
                      </div>
                    </div>
                  )}

                  <h3 className={`text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2 ${plan.featured ? 'mt-4 sm:mt-6' : ''}`}>{plan.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">
                    {plan.name === 'RTL Design' && 'RTL to synthesis flow'}
                    {plan.name === 'Physical Design' && 'End-to-end place & route'}
                    {plan.name === 'Complete ASIC' && 'Full RTL to GDSII delivery'}
                  </p>

                  <div className="mb-8">
                    {plan.price ? (
                      <>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'purple' ? '#9333ea, #4f46e5' : plan.color === 'indigo' ? '#4f46e5, #0284c7' : '#0284c7, #06b6d4'})`}}>
                            ${Math.round(plan.price / 83).toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">one-time</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'purple' ? '#9333ea, #4f46e5' : plan.color === 'indigo' ? '#4f46e5, #0284c7' : '#0284c7, #06b6d4'})`}}>
                            ${Math.round(plan.monthlySupport / 83).toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                        </div>
                      </>
                    ) : null}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Custom scoping available</p>
                  </div>

                  <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} style={{backgroundColor: `${plan.color === 'purple' ? '#f3e8ff' : plan.color === 'indigo' ? '#e0e7ff' : '#cffafe'}`}}>
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 font-bold" style={{color: `${plan.color === 'purple' ? '#9333ea' : plan.color === 'indigo' ? '#4f46e5' : '#0284c7'}`}} />
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to="/contact"
                    className={`w-full px-6 py-3 sm:py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base transform hover:-translate-y-1 text-center ${
                      plan.featured
                        ? `bg-gradient-to-r ${colors.bg} text-white hover:shadow-2xl`
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                    }`}
                  >
                    {plan.name === 'Complete ASIC' ? 'Start Project' : 'Get Started'}
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="max-w-5xl mx-auto mb-12 sm:mb-16">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-8">All Plans Include</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: '🏗️', text: 'Architecture Design' },
                { icon: '✅', text: 'Verification Plan' },
                { icon: '📊', text: 'Power Analysis' },
                { icon: '📚', text: 'Full Documentation' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="max-w-3xl mx-auto bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-2xl border border-purple-200/50 dark:border-purple-800/50 p-6 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">VLSI Services FAQ</h3>
            <div className="space-y-4 sm:space-y-5">
              {[
                {
                  q: 'What is your tape-out success rate?',
                  a: 'We maintain a 99%+ first-pass success rate with 50+ completed tape-outs. Our rigorous verification methodology and design expertise ensure silicon-ready quality.'
                },
                {
                  q: 'Which technology nodes do you support?',
                  a: 'We support 15+ technology nodes from 180nm to 5nm and below. We work with major foundries (TSMC, Samsung, GlobalFoundries) and maintain cutting-edge design tools.'
                },
                {
                  q: 'How long does ASIC design take?',
                  a: 'RTL design: 2-3 months. Complete ASIC: 6-9 months. Timelines depend on complexity. We provide detailed schedules after technical review.'
                },
                {
                  q: 'Do you provide post-silicon support?',
                  a: 'Yes! We offer post-silicon debug, bring-up assistance, firmware integration, and long-term production support packages.'
                },
                {
                  q: 'What design tools do you use?',
                  a: 'We use industry-standard tools: Synopsys, Cadence, Mentor Graphics. We are also experienced with latest open-source EDA tools.'
                }
              ].map((faq, idx) => (
                <details key={idx} className="group cursor-pointer">
                  <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <span className="text-sm sm:text-base text-left pr-4">{faq.q}</span>
                    <span className="group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
                  </summary>
                  <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white px-2 sm:px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Cpu className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 animate-bounce" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6">
            Ready to Design Your Next Chip?
          </h2>
          <p className="text-base sm:text-xl text-blue-100 mb-6 sm:mb-8 px-2">
            Partner with India's leading VLSI design team. From RTL to tape-out, we deliver excellence.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 sm:px-10 py-3 sm:py-5 bg-white text-purple-600 rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300"
          >
            <span>💻 Schedule VLSI Consultation</span>
            <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6" />
          </Link>
        </div>
      </section>
        <div className="mt-16">
          <RelatedServices currentService="vlsi" />
        </div>
    </div>
  );
}